/**
 * Shared shape for the OLV wallet AUTO TOP-UP settings, stored at
 * Firestore `users/{uid}/wallet/autoTopUp`.
 *
 * Opt-in, off by default. Kept intentionally minimal and self-contained so the
 * settings UI and the `useAutoTopUp` hook share one source of truth for the
 * shape and defaults. Mirrors the backend `AutoTopUpSettings`
 * (functions/src/autoTopUp.ts).
 */

export interface AutoTopUpSettings {
  /** Master switch. Off by default. */
  enabled: boolean;
  /** Trigger a top-up when the wallet USD balance drops below this. */
  thresholdUsd: number;
  /** USD amount to charge on each auto top-up. */
  topUpAmountUsd: number;
  /** Hard cap on successful auto-charges within any rolling 24h window. */
  maxChargesPer24h: number;
  /** Optional explicit saved card; when absent the customer's default PM is used. */
  paymentMethodId?: string;
  /** Server timestamp of the last write (Firestore Timestamp at read time). */
  updatedAt?: unknown;
}

/**
 * User-approved defaults applied when a user first enables auto top-up.
 * 2026-08-14 owner directive: initial lower-bound threshold $25, top-up
 * amount $500. `enabled: false` here is the STORED default (enrollment only
 * happens when the user confirms a top-up with the auto option left on —
 * see TopUpModal); keep it false so reading an absent doc never charges.
 */
export const AUTO_TOP_UP_DEFAULTS: Pick<
  AutoTopUpSettings,
  'enabled' | 'thresholdUsd' | 'topUpAmountUsd' | 'maxChargesPer24h'
> = {
  enabled: false,
  thresholdUsd: 25,
  topUpAmountUsd: 500,
  maxChargesPer24h: 2,
};

/** The user-adjustable fields (everything except the `enabled` switch). */
export type AutoTopUpUpdate = Partial<
  Pick<
    AutoTopUpSettings,
    'thresholdUsd' | 'topUpAmountUsd' | 'maxChargesPer24h' | 'paymentMethodId'
  >
>;
